import { useSelector, useDispatch, useStore } from 'react-redux'
import { useEffect } from 'react'
import { RootState } from 'types'
import { Suggestion, SuggestionState } from 'api/clients/SuggestionsClient'
import pickBy from 'lodash/pickBy'
import isEqual from 'lodash/isEqual'
import { SuggestionsService } from 'api/services/SuggestionsService'

export interface ArticleSuggestions {
  /*
   * Suggestion created by the current user for which the current article is
   * the base branch.
   */
  inboundSuggestedByMe: Suggestion[]

  /*
   * List of suggestions created by other users for which the current article
   * is the base branch.
   */
  inboundSuggestedByOthers: Suggestion[]

  /*
   * Suggestion created by the current user for which the current article is
   * the branch.
   */
  outboundSuggestedByMe: Suggestion | null

  /*
   * Suggestion created by the another user for which the current article is
   * the branch.
   */
  outboundSuggestedByOthers: Suggestion | null
}

const nullResult = {
  inboundSuggestedByMe: [],
  inboundSuggestedByOthers: [],
  outboundSuggestedByMe: null,
  outboundSuggestedByOthers: null,
}

export const useSuggestions = (
  userId: number | undefined,
  articleKey: string,
  forceReload: boolean = false,
  stateFilter?: SuggestionState[],
  limit?: number,
): ArticleSuggestions => {
  const dispatch = useDispatch()
  const store = useStore()

  const suggestions = useSelector((state: RootState) => {
    const articleSuggestions = pickBy(
      state.suggestions,
      (s) => s.branch.key === articleKey || s.base_branch.key === articleKey,
    )
    if (stateFilter) {
      return Object.values(articleSuggestions).filter((s) => stateFilter.includes(s.state))
    } else {
      return Object.values(articleSuggestions)
    }
  }, isEqual)

  useEffect(() => {
    if (forceReload) {
      SuggestionsService.index(store, articleKey, { items: limit })
    }
  }, [articleKey, forceReload, limit, dispatch, store])

  if (!userId) {
    return nullResult
  }

  const inbound = suggestions.filter((s) => s.base_branch.key === articleKey)
  const outbound = suggestions.find((s) => s.branch.key === articleKey)

  const inboundSuggestedByMe = inbound.filter((s) => s.suggester.id === userId)
  const inboundSuggestedByOthers = inbound.filter((s) => s.suggester.id !== userId && s.iteration > 0)

  const outboundSuggestedByMe = outbound && outbound.suggester.id === userId ? outbound : null
  const outboundSuggestedByOthers = outbound && outbound.suggester.id !== userId ? outbound : null

  return {
    inboundSuggestedByMe: inboundSuggestedByMe,
    inboundSuggestedByOthers: inboundSuggestedByOthers,
    outboundSuggestedByMe: outboundSuggestedByMe,
    outboundSuggestedByOthers: outboundSuggestedByOthers,
  }
}
